/**
 * Types for the KBF door-style SRP.
 *
 * Cards are door styles (CiC `g_door` style-feature options) and the
 * variations underneath are finishes (CiC `g_finish` options), mirroring the
 * flooring product line / color-variant grouping. Compatibility between the
 * two is derived from the catalog's hidden `door_styles` combination feature,
 * whose options each represent one orderable door × finish pairing and carry
 * a manufacturer price group (e.g. "G6").
 *
 * A dealer can carry several cabinet catalogs (one per manufacturer line);
 * every card records the catalog it came from, since door codes such as
 * `720r` repeat across catalogs.
 */

export interface DoorStyleFinish {
  /** CiC `g_finish` option code (e.g. 'cognac_maple'), or a slug of the
   * combination token when the finish has no matching `g_finish` option. */
  code: string;
  /** Display name, e.g. 'Cider Maple'. */
  name: string;
  /** Material/species bucket, e.g. 'Maple', 'Cherry', 'Painted', 'Duraform'. */
  species: string;
  /** Swatch image. Absent when the finish has no matching `g_finish` option. */
  imageUrl?: string;
  /**
   * Manufacturer price group for this door × finish combination, e.g. 'G6'.
   * A dealer pricing tier: never shown to shoppers.
   */
  priceGroup?: string;
  /**
   * Code of the door × finish combination option, which is also the code of
   * the catalog's style item for the pairing — the SKU it is quoted under
   * (e.g. '19mus' for 330S in Cider Maple). Absent when the catalog has no
   * combination feature.
   */
  styleCode?: string;
}

/** The catalog's style item behind a door × finish pairing. */
export interface DoorStyleItem {
  /** CiC item id, e.g. '92784.19mus'. */
  id: string;
  code: string;
  /** Manufacturer style code, e.g. '19MUS' — the SKU shown on the page. */
  manufCode: string;
  name: string;
  description?: string;
  /** Per-combination render, when the catalog authored one. */
  imageUrl?: string;
}

/** Everything the door-style detail page needs, shaped server-side. */
export interface DoorStyleDetails {
  /** Catalog version of the door style (same as `door.catalogVersionId`). */
  catalogVersionId: number;
  door: DoorStyleCard;
  finish: DoorStyleFinish;
  /** The style item for this pairing, when the catalog has one. */
  styleItem?: DoorStyleItem;
  /** Other door styles, the same catalog's first, for "similar" links. */
  otherDoors: DoorStyleCard[];
}

/** The SKU segment of a finish's detail URL: its style code, else its own code. */
export const doorStyleSku = (finish: DoorStyleFinish): string =>
  finish.styleCode ?? finish.code;

/**
 * Detail page of a door style in a finish:
 * `/door-styles/waypoint-living-spaces/330s/19mus`, with an optional section
 * anchor (`hash` without the `#`). The catalog segment is needed because door
 * codes repeat across catalogs.
 */
export const doorStyleHref = (
  door: Pick<DoorStyleCard, 'catalogKey' | 'code'>,
  sku: string,
  hash?: string
): string => {
  const path = `/door-styles/${[door.catalogKey, door.code, sku]
    .map((segment) => encodeURIComponent(segment.toLowerCase()))
    .join('/')}`;
  return hash ? `${path}#${hash}` : path;
};

/**
 * Door style code as shoppers see it next to a style name ('720r' → '720R').
 */
export const doorStyleCode = (door: Pick<DoorStyleCard, 'code'>): string =>
  door.code.toUpperCase();

export interface DoorStyleCard {
  /** CiC `g_door` option code, e.g. '330s'. */
  code: string;
  /**
   * Display name: the catalog's style name ('Savannah') when it has one,
   * else the door code ('330S').
   */
  name: string;
  /**
   * The style name, when the catalog gives the door one beyond its code
   * ('Savannah' for 720R). Absent in catalogs that name doors by code.
   */
  styleName?: string;
  /**
   * URL segment of the catalog the door style belongs to, e.g.
   * 'waypoint-living-spaces' (see `doorStyleHref`). Unique per dealer.
   */
  catalogKey: string;
  /** CiC catalog id, stable across versions, e.g. '777'. */
  catalogId?: string;
  /** CiC catalog version the door style belongs to. */
  catalogVersionId: number;
  /** Brand shown on the card: the manufacturer or catalog name, when known. */
  brandName?: string;
  /** Door-front render from the `g_door` option. */
  imageUrl?: string;
  /** Finishes this door style is orderable in. */
  finishes: DoorStyleFinish[];
  /** Distinct species across `finishes`. */
  species: string[];
  /**
   * Distinct price groups across `finishes`, ascending. Dealer pricing
   * tiers: kept for quoting, never shown to shoppers.
   */
  priceGroups: string[];
}

export interface DoorStyleFilterOption {
  value: string;
  label: string;
  /** Number of door styles matching this option. */
  count: number;
}

export interface DoorStyleFilterGroup {
  id: string;
  label: string;
  options: DoorStyleFilterOption[];
}

/** Well-known filter group ids produced by the backend. */
export const DoorStyleFilterIds = {
  Brand: 'brand',
  Species: 'species',
  Finish: 'finish',
  PriceGroup: 'priceGroup',
} as const;

export interface DoorStyleSrpData {
  cards: DoorStyleCard[];
  filters: DoorStyleFilterGroup[];
  /** Total orderable door × finish combinations across all cards. */
  comboCount: number;
}

/** Selected filter values per filter group id. */
export type DoorStyleSelection = Record<string, string[]>;

/** Query parameter value of a filter group, as written to the SRP URL. */
type SearchParamValue = string | string[] | null | undefined;

/**
 * Read the SRP filter selection from the URL (`?species=maple,painted`):
 * one parameter per filter group id, comma-separated values, matched to the
 * group's options without regard to case. Unknown groups and values are
 * dropped, so a stale or hand-edited link still opens the page.
 */
export const parseDoorStyleSelection = (
  read: (groupId: string) => SearchParamValue,
  groups: DoorStyleFilterGroup[]
): DoorStyleSelection => {
  const selection: DoorStyleSelection = {};
  for (const group of groups) {
    const raw = read(group.id);
    const wanted = (Array.isArray(raw) ? raw : [raw ?? ''])
      .flatMap((value) => value.split(','))
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean);
    const values = group.options
      .map((option) => option.value)
      .filter((value) => wanted.includes(value.toLowerCase()));
    if (values.length > 0) selection[group.id] = values;
  }
  return selection;
};

/**
 * Write a filter selection over existing query parameters, mirroring the
 * flooring SRP: lower-cased, comma-separated, empty groups removed. Other
 * parameters (tracking, etc.) are kept.
 */
export const doorStyleSelectionSearch = (
  current: URLSearchParams,
  groupIds: string[],
  selection: DoorStyleSelection
): URLSearchParams => {
  const next = new URLSearchParams(current);
  for (const groupId of groupIds) {
    const values = selection[groupId] ?? [];
    if (values.length === 0) next.delete(groupId);
    else
      next.set(groupId, values.map((value) => value.toLowerCase()).join(','));
  }
  return next;
};
